import { Amplify } from 'aws-amplify'
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'

const domain = (process.env.VUE_APP_COGNITO_DOMAIN || '').trim()
const clientId = (process.env.VUE_APP_COGNITO_CLIENT_ID || '').trim()
const userPoolId = (process.env.VUE_APP_COGNITO_USER_POOL_ID || '').trim()
const redirectUri = (process.env.VUE_APP_COGNITO_REDIRECT_URI || '').trim()
const CALLBACK_PATH = '/login/cognito/callback'
const scopes = (process.env.VUE_APP_COGNITO_SCOPE || 'openid email profile').trim().split(/\s+/)

function addRedirectUri (uris, uri) {
  const normalized = (uri || '').trim()
  if (normalized) uris.add(normalized)
}

/** 同时注册 www / 非 www，避免用户从不同域名发起 OAuth 时 InvalidOriginException */
function getRedirectSignInUris () {
  const uris = new Set()
  addRedirectUri(uris, redirectUri)

  if (typeof window !== 'undefined') {
    addRedirectUri(uris, `${window.location.origin}${CALLBACK_PATH}`)
  }

  if (redirectUri) {
    try {
      const url = new URL(redirectUri)
      const path = url.pathname || CALLBACK_PATH
      addRedirectUri(uris, `${url.origin}${path}`)
      if (url.hostname.startsWith('www.')) {
        addRedirectUri(uris, `${url.protocol}//${url.hostname.slice(4)}${path}`)
      } else {
        addRedirectUri(uris, `${url.protocol}//www.${url.hostname}${path}`)
      }
    } catch {
      // ignore malformed redirect uri
    }
  }

  return [...uris]
}

function getRedirectSignOutUris () {
  const uris = new Set()
  if (typeof window !== 'undefined') {
    addRedirectUri(uris, `${window.location.origin}/mine`)
  }

  if (redirectUri) {
    try {
      const url = new URL(redirectUri)
      if (url.hostname.startsWith('www.')) {
        addRedirectUri(uris, `${url.protocol}//${url.hostname.slice(4)}/mine`)
      } else {
        addRedirectUri(uris, `${url.protocol}//www.${url.hostname}/mine`)
      }
    } catch {
      // ignore malformed redirect uri
    }
  }

  return [...uris]
}

const COGNITO_PROVIDER_ALIASES = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple'
}

const SUPPORTED_COGNITO_PROVIDERS = ['Google', 'Facebook', 'Apple']
const DEFAULT_COGNITO_PROVIDERS = ['Google', 'Facebook']

function parseCognitoProviders (raw) {
  const value = (raw || '').trim()
  if (!value) return [...DEFAULT_COGNITO_PROVIDERS]

  const providers = value
    .split(',')
    .map((item) => item.trim())
    .map((item) => COGNITO_PROVIDER_ALIASES[item.toLowerCase()] || item)
    .filter((item) => SUPPORTED_COGNITO_PROVIDERS.includes(item))

  return providers.length ? providers : [...DEFAULT_COGNITO_PROVIDERS]
}

export const enabledCognitoProviders = parseCognitoProviders(process.env.VUE_APP_COGNITO_PROVIDER)

export function isCognitoProviderEnabled (provider) {
  return enabledCognitoProviders.includes(provider)
}

let configured = false

function hasRedirectOrigin() {
  return !!redirectUri || typeof window !== 'undefined'
}

export function configureAmplify() {
  if (configured) return
  if (!domain || !clientId || !userPoolId || !hasRedirectOrigin()) {
    console.warn('Amplify Auth: missing Cognito config')
    return
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
        loginWith: {
          oauth: {
            domain,
            scopes,
            redirectSignIn: getRedirectSignInUris(),
            redirectSignOut: getRedirectSignOutUris(),
            responseType: 'code'
          }
        }
      }
    }
  })
  configured = true
}

export function isAmplifyConfigured() {
  return !!(domain && clientId && userPoolId && hasRedirectOrigin())
}

export async function loginWithProvider(provider) {
  configureAmplify()
  await signInWithRedirect({ provider })
}

export async function loginWithGoogle() {
  return loginWithProvider('Google')
}

export async function loginWithFacebook() {
  return loginWithProvider('Facebook')
}

export async function loginWithApple() {
  return loginWithProvider('Apple')
}

export async function logout() {
  configureAmplify()
  await signOut()
}

export async function getCurrentSession() {
  configureAmplify()
  try {
    const user = await getCurrentUser()
    const session = await fetchAuthSession()
    return {
      user,
      tokens: session.tokens || null,
      credentials: session.credentials || null
    }
  } catch (e) {
    return null
  }
}

export async function getAccessToken() {
  const session = await getCurrentSession()
  return session?.tokens?.accessToken?.toString() || null
}

export async function getIdToken() {
  const session = await getCurrentSession()
  return session?.tokens?.idToken?.toString() || null
}
